/* global Office, OfficeRuntime, Excel, window */

const hasWindow = typeof window !== "undefined";
const hasLocalStorage = hasWindow && typeof window.localStorage !== "undefined";
const hasExcelApis = typeof Excel !== "undefined" && typeof Excel.run === "function";

const LOGIN_STORAGE_KEY = "userLoggedIn";
const TOKEN_STORAGE_KEY = "access_token";

const API_BASE_URL = hasWindow
  ? window.location.hostname.includes("costy.pro")
    ? "https://costy.pro/api"
    : "https://costy.pro/api"
  : process.env.REACT_APP_API_BASE_URL || "";

const trimTrailingSlash = (value) => String(value || "").replace(/\/$/, "");
const trimLeadingSlash = (value) => String(value || "").replace(/^\//, "");

/**
 * Office ready helper
 */
export async function ensureOfficeReady() {
  if (typeof Office === "undefined" || typeof Office.onReady !== "function") return;
  if (!Office.context || !Office.context.host) return;

  try {
    await Office.onReady();
  } catch (error) {
    console.warn("Office.onReady rejected", error);
  }
}

/**
 * Checks OfficeRuntime.storage
 */
export async function hasOfficeRuntime() {
  await ensureOfficeReady();
  return typeof OfficeRuntime !== "undefined" && !!OfficeRuntime.storage;
}

export const environment = {
  hasWindow,
  hasLocalStorage,
  hasExcelApis,
  hasOfficeRuntime,
};

export const apiUrl = (path) => {
  const base = environment.hasWindow
    ? trimTrailingSlash(API_BASE_URL)
    : trimTrailingSlash(process.env.API_BASE_URL || "");

  if (!base) throw new Error("API base URL is not configured");

  return `${base}/${trimLeadingSlash(path)}`;
};

/* =======================
   ACCESS TOKEN
======================= */

export async function storeAccessToken(token) {
  try {
    if (await hasOfficeRuntime()) {
      await OfficeRuntime.storage.setItem(TOKEN_STORAGE_KEY, token);
    } else if (hasLocalStorage) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  } catch (e) {
    console.error("storeAccessToken failed", e);
  }
}

export async function clearAccessToken() {
  try {
    if (await hasOfficeRuntime()) {
      await OfficeRuntime.storage.removeItem(TOKEN_STORAGE_KEY);
    } else if (hasLocalStorage) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("Unable to clear access token", error);
  }
}

export async function getAccessToken() {
  try {
    if (await hasOfficeRuntime()) {
      return await OfficeRuntime.storage.getItem(TOKEN_STORAGE_KEY);
    }

    if (hasLocalStorage) {
      return window.localStorage.getItem(TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("Unable to read access token", error);
  }

  return null;
}

/* =======================
   LOGIN STATE (optional)
======================= */

export async function persistLoginState(state) {
  const stringValue = state ? "true" : "false";

  try {
    if (await hasOfficeRuntime()) {
      await OfficeRuntime.storage.setItem(LOGIN_STORAGE_KEY, stringValue);
    } else if (hasLocalStorage) {
      window.localStorage.setItem(LOGIN_STORAGE_KEY, stringValue);
    }
  } catch (error) {
    console.warn("Unable to persist login state", error);
  }
}

export async function restoreLoginState() {
  try {
    if (await hasOfficeRuntime()) {
      const stored = await OfficeRuntime.storage.getItem(LOGIN_STORAGE_KEY);
      return stored === "true";
    }

    if (hasLocalStorage) {
      return window.localStorage.getItem(LOGIN_STORAGE_KEY) === "true";
    }
  } catch (error) {
    console.warn("Unable to restore login state", error);
  }

  return false;
}
